import { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import "@mantine/core/styles.css";
import "@mantine/dropzone/styles.css";
import "./App.scss";

import { Auth } from "../processes/auth/Auth";
import { Login } from "../processes/login/Login";
import useGlobalInputFocus from "../shared/components/input/hooks/useGlobalInputFocus";
import { TermsOfService } from "../pages/terms-of-service/TermsOfService";
import { PrivacyPolicy } from "../pages/privacy-policy/PrivacyPolicy";
import { Dashboard } from "../pages/dashboard/Dashboard";
import { Profile } from "../pages/profile/Profile";
import { DevelopmentPlan } from "../pages/development-plan/DevelopmentPlan";
import { Courses } from "../pages/courses/Courses";
import { Report } from "../pages/report/Report";
import { NewPasswordPage } from "../pages/new-password/NewPassword";
import LinkedInCallback from "../pages/callback-pages/LinkedinCallback";

import useAuthStore from "../store/useAuthStore";

import { PrivateRoute } from "../routes/PrivateRoute";
import { RestrictedRoute } from "../routes/RestrictedRoute";
import useProfileStore from "../store/useProfileStore";
import useReportStore from "../store/useReportStore";
import ErrorPage from "../pages/error-page/ErrorPage";
import ErrorBoundary from "../widgets/error-boundaries/ErrorBoundary";
import { useTranslation } from "react-i18next";
import useDashboardStore from "../store/useDashboardStore";
import { setProfileLanguage } from "../api/profileApi";
import ProfileEdit from "../processes/profile-edit/ProfileEdit";
import { processUserPrice } from "../api/reportApi";

export function App() {
  useGlobalInputFocus();
  const {
    fetchProfile,
    getProfileLanguage,
    profileLanguage,
    setChanges,
    setUserMarketChanges,
  } = useProfileStore();
  const { makeReport } = useReportStore();
  const { fetchDashboard } = useDashboardStore();
  const [maxStep, setMaxStep] = useState(0);
  const { i18n } = useTranslation();
  const { access_token, cvProcessing, cvId, onCvStatus, user } = useAuthStore();

  useEffect(() => {
    const localLangRaw = localStorage.getItem("profileLanguage");
    const localLang = localLangRaw
      ? Object.keys(JSON.parse(localLangRaw))[0]
      : null;

    if (localLang) {
      i18n.changeLanguage(localLang);
    }

    if (user?.isLoggedIn) {
      if (localLang) {
        setProfileLanguage(localLang).then(() => {
          getProfileLanguage();
        });
      } else {
        getProfileLanguage();
      }
    }
  }, [user?.isLoggedIn]);

  useEffect(() => {
    if (profileLanguage) {
      const backendLang = Object.keys(profileLanguage)[0];

      i18n.changeLanguage(backendLang);
    }
  }, [profileLanguage]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (cvProcessing === "processing" && cvId) {
      const checkCvStatus = async () => {
        try {
          const response = await onCvStatus(cvId, access_token!);

          if (response.data.status === "completed") {
            localStorage.setItem("cvProcessing", JSON.stringify("completed"));
            clearInterval(intervalId);
            await fetchProfile();
            await fetchDashboard(access_token!);
            await makeReport();
            processUserPrice();
            setUserMarketChanges(false);
            setChanges(false);
          }
        } catch (error) {
          clearInterval(intervalId);
        }
      };
      intervalId = setInterval(checkCvStatus, 15000);
      return () => clearInterval(intervalId);
    }

    return () => {};
  }, [cvProcessing, cvId, access_token, onCvStatus]);

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Routes>
          <Route
            path="/"
            element={
              <PrivateRoute redirectTo="/login/sign-in" component={Dashboard} />
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute redirectTo="/login/sign-in" component={Profile} />
            }
          />
          <Route
            path="/profile/edit/:page"
            element={
              <PrivateRoute
                redirectTo="/login/sign-in"
                component={ProfileEdit}
              />
            }
          />
          <Route
            path="/development-plan"
            element={
              <PrivateRoute
                redirectTo="/login/sign-in"
                component={DevelopmentPlan}
              />
            }
          />
          <Route
            path="/courses"
            element={
              <PrivateRoute redirectTo="/login/sign-in" component={Courses} />
            }
          />
          <Route
            path="/report"
            element={
              <PrivateRoute redirectTo="/login/sign-in" component={Report} />
            }
          />
          <Route
            path="/terms-of-service"
            element={
              <RestrictedRoute redirectTo="/" component={TermsOfService} />
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <RestrictedRoute redirectTo="/" component={PrivacyPolicy} />
            }
          />
          <Route
            path="/auth/:page"
            element={
              <RestrictedRoute
                redirectTo="/report"
                component={Auth}
                extraProps={{ maxStep, setMaxStep }}
              />
            }
          />
          <Route
            path="/login/:page"
            element={
              <RestrictedRoute
                redirectTo="/"
                component={Login}
                extraProps={{ setMaxStep }}
              />
            }
          />
          <Route
            path="/reset-password"
            element={
              <RestrictedRoute redirectTo="/" component={NewPasswordPage} />
            }
          />
          <Route
            path="/callback"
            element={
              <RestrictedRoute redirectTo="/" component={LinkedInCallback} />
            }
          />
          <Route path="/*" element={<ErrorPage />} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
